import {useEffect, useState} from 'react';
import Title from 'antd/es/typography/Title';

import {
  Button,
  Checkbox,
  Col,
  Input,
  InputNumber,
  Row,
  Select,
  Typography,
} from 'antd';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {Program} from '../../graphql/API';
import {PackagesService} from './PackagesService';
import {Loading} from '../../components';
import {ProgramsService} from '../Program';
import type {
  NewPackageById,
  Currency,
} from '@PikaDoApp/npm-pikado-logic/dist/modules/package/graphql/package.interface';

const packagesService = new PackagesService();
const programService = new ProgramsService();

export const PackageEdit = () => {
  const history = useHistory();
  const location: {
    state: any;
    hash: any;
    search: any;
    pathname: any;
  } = useLocation();

  const {Title: AntTitle, Text} = Typography;

  const [isError, setIsError] = useState<boolean>(false);

  let params: {packageId: string} = useParams();
  const [programPackage, setProgramPackage] = useState<NewPackageById>();
  const [programs, setPrograms] = useState<Array<Program> | []>();

  useEffect(() => {
    programService
      .getPrograms()
      .then((e) => {
        setPrograms(e);
      })
      .catch((err) => {
        console.log('Programs fetching error ->', err);
      });

    if (location.state?.package) {
      setProgramPackage(location.state.package);
    } else {
      packagesService
        .getPackageById(params.packageId)
        .then((pkg: any) => {
          console.log('Package loaded:', pkg);
          setProgramPackage(pkg);
        })
        .catch((e) => {
          setIsError(true);
        });
    }
  }, [params.packageId]);

  const onChange = (prop: string, value: any) => {
    setProgramPackage({...programPackage!, [prop]: value});
  };

  const onChangeI18n = (
    field: 'name_i18n' | 'description_i18n' | 'benefits_i18n',
    lang: 'en' | 'ar' | 'tr',
    value: string,
  ) => {
    setProgramPackage({
      ...programPackage!,
      [field]: {
        ...programPackage![field],
        [lang]: value,
      },
    });
  };

  const onChangePrice = (
    country: 'US' | 'AE' | 'EG' | 'SA' | 'QA' | 'KW' | 'TR',
    field: 'price' | 'currency',
    value: number | Currency,
  ) => {
    const currentPrice = programPackage?.price || {} as any;
    const countryPrice = currentPrice[country] || {price: 0, currency: 'USD' as Currency};

    setProgramPackage({
      ...programPackage!,
      price: {
        ...currentPrice,
        [country]: {
          ...countryPrice,
          [field]: value,
        },
      },
    });
  };

  const handleUpdate = async () => {
    if (!programPackage) return;

    try {
      const updateInput = {
        id: programPackage._id,
        input: {
          packageDuration: programPackage.packageDuration,
          programId: programPackage.programId,
          description_i18n: programPackage.description_i18n,
          name_i18n: programPackage.name_i18n,
          benefits_i18n: programPackage.benefits_i18n,
          onlyAI: programPackage.onlyAI,
          type: programPackage.type,
          price: programPackage.price,
          isLoadTest: programPackage.isLoadTest,
          isFree: programPackage.isFree,
          isActive: programPackage.isActive,
          productIAP: programPackage.productIAP || null,
        },
      };

      console.log('Updating package with:', updateInput);
      await packagesService.updatePackage(updateInput);
      alert('Package updated successfully');
      history.push('/packages');
    } catch (err: any) {
      alert('Package update failed: ' + (err.message || 'Unknown error'));
      console.error('Update error:', err);
    }
  };

  return (
    <div style={{padding: 10}}>
      <Title style={{textAlign: 'center'}}>Edit Package</Title>
      {programPackage ? (
        <>
          {/* Basic Information */}
          <Row gutter={16}>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Package Name (English)*
                </Text>
                <Input
                  value={programPackage?.name_i18n?.en || ''}
                  onChange={(e) => onChangeI18n('name_i18n', 'en', e.target.value)}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Package Name (Arabic)
                </Text>
                <Input
                  value={programPackage?.name_i18n?.ar || ''}
                  onChange={(e) => onChangeI18n('name_i18n', 'ar', e.target.value)}
                />
              </div>
            </Col>
          </Row>

          {/* Description */}
          <Row gutter={16}>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Description (English)
                </Text>
                <Input.TextArea
                  rows={3}
                  value={programPackage?.description_i18n?.en || ''}
                  onChange={(e) => onChangeI18n('description_i18n', 'en', e.target.value)}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Description (Arabic)
                </Text>
                <Input.TextArea
                  rows={3}
                  value={programPackage?.description_i18n?.ar || ''}
                  onChange={(e) => onChangeI18n('description_i18n', 'ar', e.target.value)}
                />
              </div>
            </Col>
          </Row>

          {/* Benefits */}
          <Row gutter={16}>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Benefits (English)
                </Text>
                <Input.TextArea
                  rows={3}
                  value={programPackage?.benefits_i18n?.en || ''}
                  onChange={(e) => onChangeI18n('benefits_i18n', 'en', e.target.value)}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Benefits (Arabic)
                </Text>
                <Input.TextArea
                  rows={3}
                  value={programPackage?.benefits_i18n?.ar || ''}
                  onChange={(e) => onChangeI18n('benefits_i18n', 'ar', e.target.value)}
                />
              </div>
            </Col>
          </Row>

          {/* Program and Duration */}
          <Row gutter={16}>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Program ID*
                </Text>
                <Select
                  value={programPackage?.programId}
                  showSearch
                  style={{width: '100%'}}
                  placeholder="Select a program"
                  optionFilterProp="children"
                  onChange={(e) => onChange('programId', e)}>
                  {programs?.map((program) => {
                    const programName = typeof program.name_i18n === 'string'
                      ? JSON.parse(program.name_i18n)['en']
                      : program.name_i18n?.en || program.id;
                    return (
                      <Select.Option key={program.id} value={program.id}>
                        {programName}
                      </Select.Option>
                    );
                  })}
                </Select>
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Package Duration (months)*
                </Text>
                <InputNumber
                  min={1}
                  style={{width: '100%'}}
                  value={programPackage?.packageDuration}
                  onChange={(value) => onChange('packageDuration', value)}
                />
              </div>
            </Col>
          </Row>

          {/* Package Type and Flags */}
          <Row gutter={16}>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Package Platform Type*
                </Text>
                <Select
                  value={programPackage?.type || 'STUDENT'}
                  style={{width: '100%'}}
                  onChange={(value) => onChange('type', value)}>
                  <Select.Option value="STUDENT">Student</Select.Option>
                  <Select.Option value="BUSINESS">Business</Select.Option>
                </Select>
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  Product IAP
                </Text>
                <Input
                  value={programPackage?.productIAP || ''}
                  onChange={(e) => onChange('productIAP', e.target.value || null)}
                />
              </div>
            </Col>
          </Row>

          {/* Checkboxes */}
          <Row gutter={16}>
            <Col span={6}>
              <div style={{margin: 20}}>
                <Checkbox
                  checked={programPackage?.onlyAI}
                  onChange={(e) => onChange('onlyAI', e.target.checked)}>
                  Only AI
                </Checkbox>
              </div>
            </Col>
            <Col span={6}>
              <div style={{margin: 20}}>
                <Checkbox
                  checked={programPackage?.isFree}
                  onChange={(e) => onChange('isFree', e.target.checked)}>
                  Free Package
                </Checkbox>
              </div>
            </Col>
            <Col span={6}>
              <div style={{margin: 20}}>
                <Checkbox
                  checked={programPackage?.isActive}
                  onChange={(e) => onChange('isActive', e.target.checked)}>
                  Active
                </Checkbox>
              </div>
            </Col>
            <Col span={6}>
              <div style={{margin: 20}}>
                <Checkbox
                  checked={programPackage?.isLoadTest}
                  onChange={(e) => onChange('isLoadTest', e.target.checked)}>
                  Load Test
                </Checkbox>
              </div>
            </Col>
          </Row>

          {/* Pricing Section */}
          <Title level={4} style={{marginTop: 20}}>Pricing</Title>

          {/* US Price (Required) */}
          <Row gutter={16}>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  US Price*
                </Text>
                <InputNumber
                  min={0}
                  style={{width: '100%'}}
                  value={programPackage?.price?.US?.price}
                  onChange={(value) => onChangePrice('US', 'price', value || 0)}
                />
              </div>
            </Col>
            <Col span={12}>
              <div style={{margin: 20}}>
                <Text style={{display: 'block'}} strong={true}>
                  US Currency*
                </Text>
                <Select
                  value={programPackage?.price?.US?.currency || 'USD'}
                  style={{width: '100%'}}
                  onChange={(value) => onChangePrice('US', 'currency', value as Currency)}>
                  <Select.Option value="USD">USD</Select.Option>
                  <Select.Option value="AED">AED</Select.Option>
                  <Select.Option value="EGP">EGP</Select.Option>
                  <Select.Option value="SAR">SAR</Select.Option>
                  <Select.Option value="QAR">QAR</Select.Option>
                  <Select.Option value="KWD">KWD</Select.Option>
                  <Select.Option value="TRY">TRY</Select.Option>
                </Select>
              </div>
            </Col>
          </Row>

          {/* Additional Country Prices (Optional) */}
          {['AE', 'EG', 'SA', 'QA', 'KW', 'TR'].map((country) => (
            <Row key={country} gutter={16}>
              <Col span={12}>
                <div style={{margin: 20}}>
                  <Text style={{display: 'block'}} strong={true}>
                    {country} Price (Optional)
                  </Text>
                  <InputNumber
                    min={0}
                    style={{width: '100%'}}
                    value={programPackage?.price?.[country as keyof typeof programPackage.price]?.price}
                    onChange={(value) => onChangePrice(country as any, 'price', value || 0)}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{margin: 20}}>
                  <Text style={{display: 'block'}} strong={true}>
                    {country} Currency
                  </Text>
                  <Select
                    value={programPackage?.price?.[country as keyof typeof programPackage.price]?.currency || 'USD'}
                    style={{width: '100%'}}
                    onChange={(value) => onChangePrice(country as any, 'currency', value as Currency)}>
                    <Select.Option value="USD">USD</Select.Option>
                    <Select.Option value="AED">AED</Select.Option>
                    <Select.Option value="EGP">EGP</Select.Option>
                    <Select.Option value="SAR">SAR</Select.Option>
                    <Select.Option value="QAR">QAR</Select.Option>
                    <Select.Option value="KWD">KWD</Select.Option>
                    <Select.Option value="TRY">TRY</Select.Option>
                  </Select>
                </div>
              </Col>
            </Row>
          ))}

          <Row>
            <Col span={24}>
              <div style={{margin: 20, textAlign: 'center'}}>
                <Button type="primary" size="large" onClick={handleUpdate}>
                  Update Package
                </Button>
              </div>
            </Col>
          </Row>
        </>
      ) : (
        <Loading />
      )}

      {isError && <AntTitle>Package fetching error ⚠️</AntTitle>}
    </div>
  );
};
